// src/components/signal/BasicPanel.tsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Analysis, n, BINANCE } from "../../utils/chartUtils";

type Props = {
  symbol: string;
  tf: string;
  analysis: Analysis | null;
  loadErr?: string | null;
};

// 안전 숫자
const ns = (v: any, dp = 4) => (Number.isFinite(v) ? n(v as number, dp) : "-");

const BasicPanel: React.FC<Props> = ({ symbol, tf, analysis, loadErr }) => {
  const { t } = useTranslation("signal");
  const [last, setLast] = useState<number | null>(null);
  const [chgPct, setChgPct] = useState<number | null>(null);

  // 24h 티커(현재가/등락률) — 심볼 바뀔 때 갱신
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BINANCE}/api/v3/ticker/24hr?symbol=${encodeURIComponent(symbol)}`);
        const j = await r.json();
        if (!alive) return;
        setLast(Number(j?.lastPrice ?? j?.weightedAvgPrice ?? NaN));
        setChgPct(Number(j?.priceChangePercent ?? NaN));
      } catch {
        setLast(null);
        setChgPct(null);
      }
    })();
    return () => { alive = false; };
  }, [symbol]);

  /* 레이블/문구 (i18n) */
  const biasText =
    !analysis
      ? "-"
      : analysis.dir === "long"
      ? t("trend.long", "상승(롱 우세)")
      : analysis.dir === "short"
      ? t("trend.short", "하락(숏 우세)")
      : t("trend.wait", "횡보(대기)");

  const volLabel = !analysis
    ? "-"
    : analysis.volReg === "high"
    ? t("basic.vol.high", "높음")
    : analysis.volReg === "low"
    ? t("basic.vol.low", "낮음")
    : t("basic.vol.neutral", "중립");

  const volTip = !analysis
    ? ""
    : analysis.volReg === "high"
    ? t("basic.vol.tip.high", "변동성 높음: 손절 폭을 넓히거나 진입 수량 축소.")
    : analysis.volReg === "low"
    ? t("basic.vol.tip.low", "변동성 낮음: 돌파 실패 가능성↑, 분할 진입 권장.")
    : t("basic.vol.tip.neutral", "변동성 중립: 기본 플랜 그대로 적용.");

  const planHint = !analysis
    ? ""
    : analysis.dir === "long"
    ? t("basic.plan.long", "상승장: 지지대 눌림 분할 매수, 목표/손절은 ATR 기반.")
    : analysis.dir === "short"
    ? t("basic.plan.short", "하락장: 저항 재돌파 실패(하락 반등) 시 매도, 지지대 근처 청산.")
    : t("basic.plan.wait", "혼조: 밴드 내부 추격 금지, 명확한 돌파/이탈 대기.");

  return (
    <div className="card">
      {/* 상단 티커 */}
      <div className="basic-ticker">
        <div className="left">
          <span className="sym">{symbol}</span>
          <span className="tfchip">{tf}</span>
        </div>
        <div className="right">
          <span className="price">{last != null && Number.isFinite(last) ? n(last, 4) : "-"}</span>
          <span
            className={`pct ${chgPct == null || !Number.isFinite(chgPct) ? "none" : chgPct > 0 ? "up" : chgPct < 0 ? "down" : "none"}`}
            title={t("basic.ticker.changeTitle", "24시간 등락률")}
          >
            {chgPct == null || !Number.isFinite(chgPct) ? "—" : `${n(chgPct, 2)}%`}
          </span>
        </div>
      </div>

      <div className="card-head neon">{t("basic.title", "기초 분석")}</div>

      {loadErr && <div className="err">{t("error.load", "데이터 로드 실패")} · {loadErr}</div>}

      {!analysis ? (
        <p className="muted">{t("loading", "불러오는 중")}</p>
      ) : (
        <div className="kgrid">
          {/* 트렌드 */}
          <section className="kcard">
            <header>📈 {t("basic.flow", "현재 흐름")}</header>
            <div className={`bias ${analysis.dir}`}>{biasText}</div>
            <p className="hint">
              {t("basic.flow.hint", "손절 폭은 ATR(14)을 참고하고, 목표는 보수적으로 설정하세요.")}
            </p>
          </section>

          {/* S/R */}
          <section className="kcard">
            <header>🧱 {t("basic.sr", "지지/저항")}</header>
            <div className="row">
              <span className="kk">{t("basic.support", "지지(S)")}</span>
              <span className="vv">{ns(analysis.sup, 4)}</span>
            </div>
            <div className="row">
              <span className="kk">{t("basic.resistance", "저항(R)")}</span>
              <span className="vv">{ns(analysis.res, 4)}</span>
            </div>
          </section>

          {/* ATR */}
          <section className="kcard">
            <header>⚡ {t("basic.atr", "ATR")}</header>
            <div className="row">
              <span className="kk">{t("basic.atr14", "ATR(14)")}</span>
              <span className="vv">{ns(analysis.atr, 4)}</span>
            </div>
            <p className="hint">{t("basic.atr.hint", "손절/익절 간격 산정에 사용.")}</p>
          </section>

          {/* 변동성 */}
          <section className="kcard">
            <header>🌪 {t("basic.vol.title", "변동성")}</header>
            <div className="badge">{volLabel}</div>
            <p className="hint">{volTip}</p>
          </section>

          {/* 리스크 */}
          <section className="kcard">
            <header>🛡 {t("basic.risk", "리스크 팁")}</header>
            <p className="hint">
              {t("basic.risk.hint", "한 트레이드당 계정의 1–2% 내에서 리스크 관리. 과도한 레버리지는 지양.")}
            </p>
          </section>

          {/* 플랜 힌트 */}
          <section className="kcard kfull">
            <header>📝 {t("basic.plan.title", "플랜 힌트")}</header>
            <p className="hint">{planHint}</p>
          </section>
        </div>
      )}

      {/* 전용 스타일(넘침 방지 포함) */}
      <style>{`
.basic-ticker{
  display:flex;justify-content:space-between;align-items:center;
  border:1px solid #263046;background:#0f1725;border-radius:12px;
  padding:8px 10px;margin-bottom:8px;gap:8px;min-width:0;
}
.basic-ticker .left{display:flex;align-items:center;gap:8px;min-width:0}
.sym{font-weight:900;font-size:16px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:38vw}
.tfchip{border:1px solid #314055;background:#0b1220;border-radius:999px;padding:3px 8px;font-size:12px}
.basic-ticker .right{display:flex;align-items:center;gap:8px}
.price{font-weight:900}
.pct{border-radius:999px;padding:3px 8px;font-size:12px;border:1px solid #3b475c}
.pct.up{border-color:#00ff00;color:#00ff00}
.pct.down{border-color:#ff0000;color:#ff0000}
.pct.none{opacity:.7}

.kgrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:10px}
.kcard{background:#0b1220;border:1px solid #223046;border-radius:14px;padding:12px;display:flex;flex-direction:column;gap:6px}
.kcard header{font-weight:900;color:#cfe1ff}
.kcard .row{display:flex;justify-content:space-between;gap:10px}
.kk{opacity:.8;font-size:12px}
.vv{font-weight:900}
.kcard .hint{opacity:.85;font-size:13px;margin:2px 0 0}
.kcard .badge{display:inline-flex;align-items:center;border:1px solid #2f3f58;border-radius:8px;padding:4px 8px;font-weight:800;width:max-content}
.kfull{grid-column:1/-1}
.bias{display:inline-flex;align-items:center;font-weight:900;border-radius:8px;padding:4px 8px;border:1px solid #2e3b52;width:max-content}
.bias.long{color:#00ff00;border-color:#00ff00}
.bias.short{color:#ff0000;border-color:#ff0000}
.bias.wait{color:#e5e7eb;border-color:#3a475f}
@media (max-width: 520px){
  .sym{max-width:52vw}
}
      `}</style>
    </div>
  );
};

export default BasicPanel;
